import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, FileText, ImageOff, Film } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDuration } from "./duration";
import { isImage, isVideo, type MediaItem } from "./types";

interface MediaPreviewProps {
  item: MediaItem;
  /** Sizing / aspect-ratio comes from the parent (e.g. "aspect-square"). */
  className?: string;
  /** When set, the tile is an interactive button that calls this on click/Enter
   *  (typically to open the lightbox). When omitted, the tile is display-only
   *  and lets clicks pass through to a parent control (e.g. a select button). */
  onOpen?: () => void;
  /** Desktop hover plays a muted inline preview of videos. Default true. */
  hoverPlay?: boolean;
  /** Videos start playing (muted, looping, with controls) as soon as the tile
   *  mounts, for previews that are already on screen because the user asked
   *  to see them (e.g. a hover card). Takes precedence over `hoverPlay`. */
  autoPlay?: boolean;
  /** aria-label for the interactive tile. Falls back to the filename. */
  label?: string;
}

/**
 * Reusable media tile: lazy image with broken-image fallback, or a video with
 * a poster, ▶ overlay, duration badge, and desktop hover-to-play (muted).
 * Document/unknown types render an icon tile. Clicking (when `onOpen` is set)
 * opens the full lightbox player.
 */
export function MediaPreview({
  item,
  className,
  onOpen,
  hoverPlay = true,
  autoPlay = false,
  label,
}: MediaPreviewProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hovering = useRef(false);
  const [loadError, setLoadError] = useState(false);
  const [posterError, setPosterError] = useState(false);
  const [playing, setPlaying] = useState(false);

  const handleEnter = () => {
    if (!hoverPlay || autoPlay || !isVideo(item)) return;
    const v = videoRef.current;
    if (!v) return;
    hovering.current = true;
    v.currentTime = 0;
    void v
      .play()
      // Only reflect "playing" if the pointer is still over the tile — guards
      // against the play() promise resolving after the user already left.
      .then(() => {
        if (hovering.current) setPlaying(true);
      })
      .catch(() => {});
  };

  const handleLeave = () => {
    hovering.current = false;
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    // With a usable stored poster, the <img> overlay covers the frame. Without
    // one, return to the ~1s seek so the resting thumbnail isn't a black frame 0.
    v.currentTime = item.thumbnailUrl && !posterError ? 0 : 1;
    setPlaying(false);
  };

  const duration = formatDuration(item.durationSec);
  const showImage = isImage(item) && !loadError;
  const showVideo = isVideo(item) && !loadError;

  let inner: React.ReactNode;
  if (showVideo && autoPlay) {
    inner = (
      <video
        src={item.url}
        poster={item.thumbnailUrl ?? undefined}
        autoPlay
        muted
        loop
        playsInline
        controls
        preload="auto"
        onError={() => setLoadError(true)}
        className="h-full w-full bg-black object-contain"
      />
    );
  } else if (showImage) {
    inner = (
      <img
        src={item.thumbnailUrl ?? item.url}
        alt={item.filename ?? ""}
        loading="lazy"
        onError={() => setLoadError(true)}
        className="h-full w-full object-cover"
      />
    );
  } else if (showVideo) {
    // With a stored poster: show it via an <img> overlay (the <video> `poster`
    // attribute disappears after the first play, so it can't survive a hover)
    // and skip preloading. Without one: load metadata and seek ~1s in via a
    // media fragment so the browser paints a real frame as the thumbnail.
    // A broken poster falls back to the live-frame path (no broken-image tile).
    const hasPoster = !!item.thumbnailUrl && !posterError;
    const src = hasPoster || item.url.includes("#") ? item.url : `${item.url}#t=1`;
    inner = (
      <>
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload={hasPoster ? "none" : "metadata"}
          onError={() => setLoadError(true)}
          className="h-full w-full object-cover"
        />
        {hasPoster && !playing && (
          <img
            src={item.thumbnailUrl!}
            alt=""
            aria-hidden="true"
            onError={() => setPosterError(true)}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        )}
        {/* Play affordance — hidden while the hover preview is running. */}
        {!playing && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm">
              <Play className="h-4 w-4 translate-x-[1px] fill-white text-white" />
            </span>
          </span>
        )}
        {duration && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white"
          >
            {duration}
          </span>
        )}
      </>
    );
  } else {
    // Broken media or document/unknown type → icon tile.
    const Icon = isVideo(item) ? Film : isImage(item) ? ImageOff : FileText;
    inner = (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
        <Icon className="h-6 w-6" />
        {item.filename && (
          <span className="max-w-full truncate px-2 text-[10px]">{item.filename}</span>
        )}
      </div>
    );
  }

  const base = "relative block overflow-hidden rounded-lg bg-muted";

  if (onOpen) {
    return (
      <button
        type="button"
        onClick={onOpen}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        aria-label={label ?? item.filename ?? t("media.open")}
        className={cn(
          base,
          "group cursor-pointer ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          className,
        )}
      >
        {inner}
      </button>
    );
  }

  return (
    <div onMouseEnter={handleEnter} onMouseLeave={handleLeave} className={cn(base, className)}>
      {inner}
    </div>
  );
}
